import { useIntl } from "react-intl";
import { FocusContext, type FocusedElement } from "src/FocusContext";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { SearchInput } from "src/components/SearchInput/SearchInput";
import { useNavigateToHotelsPage } from "src/utils/hooks/useNavigateToHotelsPage";
import useSearch from "src/utils/hooks/useSearch";
import {
  useSearchInput,
  useSearchKeyboard,
} from "src/utils/hooks/useSearchInput";
import messages from "src/components/AutocompleteInput/AutocompleteInput.messages";
import hotelMessages from "../HotelSearch.messages";

export function DestinationInput() {
  const { origin, destination } = useSearch();
  const originCanonical = origin?.canonicalName; // The origin cannot be updated from within the Hotels Screen.
  const { navigateToHotels } = useNavigateToHotelsPage();
  const intl = useIntl();
  const placeholder = intl.formatMessage(messages.destinationPlaceholder);

  const keyboard = useSearchKeyboard();
  const search = useSearchInput(
    {
      id: "destination",
      place: destination,
      onSelect: handleDestinationAutocompleteSelect,
    },
    keyboard,
  );

  function handleDestinationAutocompleteSelect(place: AutocompletePlace) {
    // Hotels search differs to LargeSearchBar: automatically initiate search request when an autocomplete item is clicked.
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Initiate:hotelListRequest",
      label: "DestinationChanged",
    });
    navigateToHotels({
      originCanonical: originCanonical ?? "Hotels-near",
      destinationCanonical: place?.canonicalName ?? destination?.canonicalName,
    });

    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:destination-input",
      label: "select-option",
    });
  }

  function handleFocusChange(focusedElement: FocusedElement) {
    keyboard.setFocusedElement(focusedElement, search.displayResults.length);
  }

  return (
    <FocusContext
      value={{
        focusedElement: keyboard.focusedElement,
        onFocusChanged: handleFocusChange,
      }}
    >
      <SearchInput
        className="flex-wrap lg:w-90"
        search={search}
        keyboard={keyboard}
        id="hotelDestination"
        placeholder={placeholder}
        label={intl.formatMessage(hotelMessages.destinationLabel)}
      />
    </FocusContext>
  );
}
